import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { X, Loader, FolderPlus } from "lucide-react";
import { toast } from "sonner";
import { getProjects, addSessionToProject } from "../../service/apiService";
import { ProjectsService } from "../Layout/Projects"; 
import { useAuth } from '../../context/AuthContext';

interface Project {
  id: string;
  project_name: string;
  session_ids: string[];
}

interface AddToProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string | null;
}

const AddToProjectDialog: React.FC<AddToProjectDialogProps> = ({
  isOpen,
  onClose,
  sessionId
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAddingToProject, setIsAddingToProject] = useState<boolean>(false);
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  useEffect(() => {
  // Only fetch when dialog is open, sessionId exists, and auth is complete
  if (isOpen && sessionId && !authLoading && isAuthenticated) {
    fetchProjects();
  }
}, [isOpen, sessionId, authLoading, isAuthenticated]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedProjectId(null);
    }
  }, [isOpen]);

  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const projectsData = await getProjects();
      setProjects(Array.isArray(projectsData) ? projectsData : []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle adding the session to a project
  const handleAddToProject = async () => {
    if (!sessionId || !selectedProjectId) {
      toast.error("Please select a project");
      return;
    }
    
    setIsAddingToProject(true);
    
    try {
      await addSessionToProject(selectedProjectId, sessionId);
      toast.success("Conversation added to project successfully");
      
      // Automatically refresh the projects list
      ProjectsService.refresh();
      
      onClose();
    } catch (error) {
      console.error("Error adding conversation to project:", error);
      toast.error("Failed to add conversation to project");
    } finally {
      setIsAddingToProject(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 scrim" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative bg-surface-overlay text-primary rounded-lg px-6 py-4 text-left align-middle shadow-xl ring-1 ring-black/5 dark:ring-gray-700 transition-all w-full max-w-md">
                <div className="flex justify-between items-center border-b border-gray-200 pb-3 mb-4">
                  <h2 className="text-lg font-semibold">Add to Project</h2>
                  <button
                    onClick={onClose}
                    className="rounded-full bg-transparent hover:bg-gray-200 focus:outline-none p-1"
                  >
                    <X size={18} />
                  </button>
                </div>
                
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select a project
                  </label>
                  {isLoading ? (
                    <div className="py-10 flex justify-center">
                      <Loader className="animate-spin" size={24} />
                    </div>
                  ) : projects.length === 0 ? (
                    <div className="py-3 text-center text-gray-500 text-sm">
                      No projects available. Create a project first.
                    </div>
                  ) : (
                    <div className="max-h-60 overflow-y-auto border rounded-md">
                      {projects.map((project) => (
                        <div
                          key={project.id}
                          className={`px-3 py-2 cursor-pointer hover:bg-violet-50 ${
                            selectedProjectId === project.id
                              ? "bg-violet-100 border-l-4 border-violet-500"
                              : ""
                          }`}
                          onClick={() => setSelectedProjectId(project.id)}
                        >
                          <div className="font-medium">{project.project_name}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end space-x-2">
                  <button
                    className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    className={`px-3 py-2 text-sm text-white bg-violet-600 rounded-md hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 ${
                      !selectedProjectId || isAddingToProject || isLoading
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    onClick={handleAddToProject}
                    disabled={!selectedProjectId || isAddingToProject || isLoading}
                  >
                    {isAddingToProject ? (
                      <span className="flex items-center">
                        <Loader size={14} className="animate-spin mr-2" />
                        Adding...
                      </span>
                    ) : (
                      "Add to Project"
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AddToProjectDialog;